import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Link as MuiLink,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UserApi from '../../api/UserApi';
import { FullUserDto } from '../../interfaces/UserInterfaces';
import { ContextTicketDto } from '../HomeView/HomeView';
import { deriveStageLabel, isClosedStage } from '../../utils/caseStage';
import { clientDisplayName } from '../../utils/clientName';
import './ManagerUsersView.scss';
import { formatDateOnly } from '../../utils/formatDateOnly';

interface SearchFilters {
  /** Any part of the client's name — the server matches it against first and last name alike. */
  User_Name: string;
  User_Email: string;
  User_CreatedAt_From: string;
  User_CreatedAt_To: string;
}

type SortBy = 'User_LastName' | 'User_FirstName' | 'User_CreatedAt' | 'User_IntakeDate';
type SortDir = 'asc' | 'desc';

const initialFilters: SearchFilters = {
  User_Name: '',
  User_Email: '',
  User_CreatedAt_From: '',
  User_CreatedAt_To: ''
};

const SORT_BY_VALUES: SortBy[] = [
  'User_LastName',
  'User_FirstName',
  'User_CreatedAt',
  'User_IntakeDate',
];

/**
 * Newest client first (Lloyd, Aug 6: "have it default to most recently added").
 *
 * One default for the whole firm — he was explicit that he did not want a per-manager preference,
 * only that everyone starts on the most recent. Staff can still sort any other way; this is only
 * where the page opens.
 *
 * It used to open on `User_CreatedAt`, which is the day the row was loaded rather than the day the
 * client arrived: 3,257 of about 4,111 clients sit on three bulk-import dates, so most of the list
 * was in no meaningful order at all. `User_IntakeDate` is their earliest case, which is a real
 * date, and Lloyd was told on 21 Sep that the order would change when the column landed.
 *
 * Clients with no case have no intake date and sort last, never first. The backend asks for
 * NULLS LAST for exactly that reason.
 */
const DEFAULT_SORT_BY: SortBy = 'User_IntakeDate';

/**
 * The sort to retry with when the API refuses the one we asked for.
 *
 * Every version of this API has accepted `User_LastName`. `User_IntakeDate` is new, and the
 * frontend and the API are separate containers that do not restart in lockstep.
 */
const FALLBACK_SORT_BY: SortBy = 'User_LastName';
const DEFAULT_SORT_DIR: SortDir = 'desc';

// "All" is modelled as a very large page window so the existing offset paging
// returns everything in one fetch (and the Load More button then stays hidden).
const ALL_PAGE_SIZE = 100000;

// Default page size (Lloyd, Jul 8: show more clients on first load — was 20, now 50).
const DEFAULT_PAGE_SIZE = 50;

// Cases-per-client fetch cap when expanding a client. The tickets endpoint defaults to 20 when no
// size is passed, which silently hid cases on clients with many matters; expand fetches all.
const EXPANDED_CASES_PAGE_SIZE = 500;

function parseUrlState(params: URLSearchParams): {
  filters: SearchFilters;
  sortBy: SortBy;
  sortDir: SortDir;
  pageSize: number;
} {
  const sortByParam = params.get('sortBy') as SortBy | null;
  const sortBy =
    sortByParam && SORT_BY_VALUES.includes(sortByParam) ? sortByParam : DEFAULT_SORT_BY;
  // Read explicitly rather than defaulting to 'asc', so a shared URL that says asc still means asc
  // now that the default has flipped to desc.
  const sortDirParam = params.get('sortDir');
  const sortDir: SortDir =
    sortDirParam === 'asc' ? 'asc' : sortDirParam === 'desc' ? 'desc' : DEFAULT_SORT_DIR;

  const pageSizeParam = params.get('pageSize');
  let pageSize = DEFAULT_PAGE_SIZE;
  if (pageSizeParam === 'all') {
    pageSize = ALL_PAGE_SIZE;
  } else if (pageSizeParam) {
    const parsed = Number(pageSizeParam);
    if (parsed === 20 || parsed === 50 || parsed === 100) pageSize = parsed;
  }

  return {
    filters: {
      // `firstName`/`lastName` are the old split params — bookmarks and older links still carry
      // them, so fold whichever is present into the single name search.
      User_Name: (
        params.get('name') ||
        [params.get('firstName'), params.get('lastName')].filter(Boolean).join(' ') ||
        ''
      ).trim(),
      User_Email: (params.get('email') || '').trim(),
      User_CreatedAt_From: (params.get('hiredFrom') || '').trim(),
      User_CreatedAt_To: (params.get('hiredTo') || '').trim(),
    },
    sortBy,
    sortDir,
    pageSize,
  };
}

function buildUrlParams(
  searchFilters: SearchFilters,
  sortByArg: SortBy,
  sortDirArg: SortDir,
  size: number,
): URLSearchParams {
  const params = new URLSearchParams();

  if (searchFilters.User_Name.trim()) {
    params.set('name', searchFilters.User_Name.trim());
  }
  if (searchFilters.User_Email.trim()) {
    params.set('email', searchFilters.User_Email.trim());
  }
  if (searchFilters.User_CreatedAt_From.trim()) {
    params.set('hiredFrom', searchFilters.User_CreatedAt_From.trim());
  }
  if (searchFilters.User_CreatedAt_To.trim()) {
    params.set('hiredTo', searchFilters.User_CreatedAt_To.trim());
  }
  if (sortByArg !== DEFAULT_SORT_BY) {
    params.set('sortBy', sortByArg);
  }
  if (sortDirArg !== DEFAULT_SORT_DIR) {
    params.set('sortDir', sortDirArg);
  }
  if (size !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', size === ALL_PAGE_SIZE ? 'all' : String(size));
  }

  return params;
}

const ManagerUsersView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState<FullUserDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentFrom, setCurrentFrom] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] = useState<SortDir>(DEFAULT_SORT_DIR);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [expandingAll, setExpandingAll] = useState(false);

  // How many clients came in on each date (Lloyd, 21 Sep: "a bird's eye view of how many clients
  // were entered day by day"). Fetched on first open rather than with the list, since it reads
  // every client and most visits to this screen never ask for it.
  const [intakeByDayOpen, setIntakeByDayOpen] = useState(false);
  const [intakeByDay, setIntakeByDay] = useState<Array<{ date: string; count: number }> | null>(null);
  const [intakeByDayLoading, setIntakeByDayLoading] = useState(false);
  const [intakeByDayError, setIntakeByDayError] = useState<string | null>(null);

  const toggleIntakeByDay = async () => {
    const opening = !intakeByDayOpen;
    setIntakeByDayOpen(opening);
    if (!opening || intakeByDay || intakeByDayLoading) return;
    setIntakeByDayLoading(true);
    setIntakeByDayError(null);
    try {
      setIntakeByDay(await UserApi.getClientIntakeByDay());
    } catch (err) {
      setIntakeByDayError(err instanceof Error ? err.message : 'Failed to load clients by day');
    } finally {
      setIntakeByDayLoading(false);
    }
  };

  // Inline expandable cases per client (Lloyd, Jul 8): show each client's cases indented under
  // the name without opening the profile. Clicking a case row goes straight to the case detail —
  // this is the 2-level flow (Clients list → case detail), no intermediate profile screen.
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [casesByUser, setCasesByUser] = useState<Record<string, ContextTicketDto[]>>({});
  const [casesLoadingFor, setCasesLoadingFor] = useState<Set<string>>(new Set());

  const fetchCasesForUser = async (userGuid: string) => {
    if (casesByUser[userGuid]) return; // already loaded
    setCasesLoadingFor((prev) => new Set(prev).add(userGuid));
    try {
      const response = await fetch(
        `${process.env.API_HOST || ''}/api/v1/tickets?User_guid=${userGuid}&size=${EXPANDED_CASES_PAGE_SIZE}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' },
      );
      if (!response.ok) throw new Error(`Failed to fetch cases: ${response.status}`);
      const data: ContextTicketDto[] = await response.json();
      setCasesByUser((prev) => ({ ...prev, [userGuid]: data }));
    } catch (err) {
      console.error('Error fetching cases for client:', err);
      setCasesByUser((prev) => ({ ...prev, [userGuid]: [] }));
    } finally {
      setCasesLoadingFor((prev) => {
        const next = new Set(prev);
        next.delete(userGuid);
        return next;
      });
    }
  };

  const toggleExpanded = (userGuid: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userGuid)) {
        next.delete(userGuid);
      } else {
        next.add(userGuid);
        fetchCasesForUser(userGuid);
      }
      return next;
    });
  };

  // Expand all currently loaded clients at once (Lloyd, Jul 8): rather than clicking each chevron,
  // open every visible client and lazily fetch cases for any not already cached, in parallel.
  const handleExpandAll = async () => {
    const guids = users.map((u) => u.User_guid);
    setExpandedUsers(new Set(guids));
    const toFetch = guids.filter((g) => !casesByUser[g]);
    if (toFetch.length === 0) return;
    setExpandingAll(true);
    try {
      await Promise.all(toFetch.map((g) => fetchCasesForUser(g)));
    } finally {
      setExpandingAll(false);
    }
  };

  // Collapse all rows; cached cases are kept so re-expanding is instant.
  const handleCollapseAll = () => {
    setExpandedUsers(new Set());
  };

  const allExpanded = users.length > 0 && users.every((u) => expandedUsers.has(u.User_guid));

  const fetchUsers = useCallback(
    async (
      searchFilters = filters,
      from = 0,
      appendResults = false,
      size = pageSize,
      sortByArg = sortBy,
      sortDirArg = sortDir
    ) => {
      if (!appendResults) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      try {
        const to = from + size;
        const params = {
          from,
          to,
          sortBy: sortByArg,
          sortDir: sortDirArg,
          ...(searchFilters.User_Name.trim() && { User_Name: searchFilters.User_Name.trim() }),
          ...(searchFilters.User_Email.trim() && { User_Email: searchFilters.User_Email.trim() }),
          ...(searchFilters.User_CreatedAt_From.trim() && { User_CreatedAt_From: searchFilters.User_CreatedAt_From.trim() }),
          ...(searchFilters.User_CreatedAt_To.trim() && { User_CreatedAt_To: searchFilters.User_CreatedAt_To.trim() })
        };

        /*
         * Fall back to a sort every API version knows, rather than showing no clients at all.
         *
         * `sortBy` is sent on every call, and `User_IntakeDate` is a value the server only learned
         * on 21 Sep 2026. If the frontend container restarts before the API — they are separate
         * services in the same compose file — the old validator rejects it outright and the whole
         * screen renders an error with zero clients, for every manager, every visit, until the API
         * catches up. A list in the wrong order is enormously better than no list.
         *
         * `User_LastName` is the fallback because every version of the API has accepted it.
         */
        let data: FullUserDto[];
        try {
          data = await UserApi.getUsersForManager(params);
        } catch (err) {
          if (sortByArg !== FALLBACK_SORT_BY) {
            console.warn(
              `Clients list: the API rejected sortBy=${sortByArg}, retrying with ${FALLBACK_SORT_BY}.`,
              err,
            );
            data = await UserApi.getUsersForManager({ ...params, sortBy: FALLBACK_SORT_BY });
            setSortBy(FALLBACK_SORT_BY);
          } else {
            throw err;
          }
        }

        if (appendResults) {
          setUsers(prevUsers => [...prevUsers, ...data]);
        } else {
          setUsers(data);
        }

        // If we got a full page back, there may be more to load.
        setHasMoreData(data.length === size);
        setCurrentFrom(from);
        setHasSearched(true);
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError(err instanceof Error ? err.message : 'Failed to load clients');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filters, pageSize, sortBy, sortDir]
  );

  const syncUrl = useCallback(
    (
      searchFilters: SearchFilters,
      sortByArg: SortBy,
      sortDirArg: SortDir,
      size: number,
    ) => {
      setSearchParams(buildUrlParams(searchFilters, sortByArg, sortDirArg, size), { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    // Restore full state from the URL (filters, sort, page size). parseUrlState also reads the
    // dashboard client-search handoff (/users?name=...) into the name filter.
    const { filters: urlFilters, sortBy: urlSortBy, sortDir: urlSortDir, pageSize: urlPageSize } =
      parseUrlState(searchParams);
    setFilters(urlFilters);
    setSortBy(urlSortBy);
    setSortDir(urlSortDir);
    setPageSize(urlPageSize);
    fetchUsers(urlFilters, 0, false, urlPageSize, urlSortBy, urlSortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    setCurrentFrom(0);
    setHasMoreData(false);
    syncUrl(filters, sortBy, sortDir, pageSize);
    fetchUsers(filters, 0, false);
  };

  const handleLoadMore = () => {
    const nextFrom = currentFrom + pageSize;
    fetchUsers(filters, nextFrom, true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentFrom(0);
    setHasMoreData(false);
    setError(null);
    syncUrl(initialFilters, sortBy, sortDir, pageSize);
    fetchUsers(initialFilters, 0, false);
  };

  const handleSortByChange = (value: SortBy) => {
    setSortBy(value);
    setCurrentFrom(0);
    syncUrl(filters, value, sortDir, pageSize);
    fetchUsers(filters, 0, false, pageSize, value, sortDir);
  };

  // Clicking a column header sorts by it, and clicking the one already sorted flips the direction.
  // A new column opens on descending, which for a date means newest first.
  const handleSortHeaderClick = (column: SortBy) => {
    const nextDir: SortDir = sortBy === column && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(column);
    setSortDir(nextDir);
    setCurrentFrom(0);
    syncUrl(filters, column, nextDir, pageSize);
    fetchUsers(filters, 0, false, pageSize, column, nextDir);
  };

  const handleSortDirChange = (value: SortDir) => {
    setSortDir(value);
    setCurrentFrom(0);
    syncUrl(filters, sortBy, value, pageSize);
    fetchUsers(filters, 0, false, pageSize, sortBy, value);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setCurrentFrom(0);
    syncUrl(filters, sortBy, sortDir, value);
    fetchUsers(filters, 0, false, value, sortBy, sortDir);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  // Stop the row's navigate handler firing when a manager clicks an email/phone link.
  const stop = (event: React.MouseEvent) => event.stopPropagation();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateTime: Date | string) => {
    try {
      const date = new Date(dateTime);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Clients
          </Typography>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/create-user')}
            color="primary"
          >
            Create Client
          </Button>
        </Box>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Clients
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            {/* One name box rather than first/last: staff type whatever part of the name they
                have, and a first name typed into a last-name box found nothing (Ashley, Aug 18). */}
            <TextField
              label="Client Name"
              placeholder="Any part of the name"
              value={filters.User_Name}
              onChange={(e) => handleFilterChange('User_Name', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
            />

            <TextField
              label="Email"
              value={filters.User_Email}
              onChange={(e) => handleFilterChange('User_Email', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 250, flex: 1 }}
            />
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Hired From"
              value={filters.User_CreatedAt_From}
              onChange={(e) => handleFilterChange('User_CreatedAt_From', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />

            <TextField
              label="Hired To"
              value={filters.User_CreatedAt_To}
              onChange={(e) => handleFilterChange('User_CreatedAt_To', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              sx={{ width: 150 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Clients
            </Button>

            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Sort + page size */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="sort-by-label">Sort by</InputLabel>
            <Select
              labelId="sort-by-label"
              label="Sort by"
              value={sortBy}
              onChange={(e) => handleSortByChange(e.target.value as SortBy)}
            >
              <MenuItem value="User_LastName">Last name</MenuItem>
              <MenuItem value="User_FirstName">First name</MenuItem>
              <MenuItem value="User_IntakeDate">Date hired</MenuItem>
              {/*
                This said "Date hired" until 21 Sep and it is not. It is the day the record was
                created, which for 3,257 of about 4,111 clients is a bulk-import date. Lloyd read
                the old label and reasonably concluded the list was ordered by date of hire.
              */}
              <MenuItem value="User_CreatedAt">Date added to system</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="sort-dir-label">Order</InputLabel>
            <Select
              labelId="sort-dir-label"
              label="Order"
              value={sortDir}
              onChange={(e) => handleSortDirChange(e.target.value as SortDir)}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="page-size-label">Show</InputLabel>
            <Select
              labelId="page-size-label"
              label="Show"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={ALL_PAGE_SIZE}>All</MenuItem>
            </Select>
          </FormControl>

          {/* Expand all / Collapse all (Lloyd, Jul 8) — open every loaded client's cases at once. */}
          <Button
            variant="outlined"
            size="small"
            onClick={allExpanded ? handleCollapseAll : handleExpandAll}
            disabled={loading || users.length === 0 || expandingAll}
            startIcon={
              expandingAll ? (
                <CircularProgress size={16} />
              ) : allExpanded ? (
                <KeyboardArrowDownIcon />
              ) : (
                <KeyboardArrowRightIcon />
              )
            }
          >
            {expandingAll
              ? `Loading cases for ${users.length} clients…`
              : allExpanded
              ? 'Collapse all'
              : 'Expand all'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={toggleIntakeByDay}
            startIcon={intakeByDayOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          >
            Clients by day
          </Button>
        </Box>

        <Collapse in={intakeByDayOpen} timeout="auto" unmountOnExit>
          <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Clients by date hired
            </Typography>
            {intakeByDayLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : intakeByDayError ? (
              <Alert severity="error">{intakeByDayError}</Alert>
            ) : !intakeByDay || intakeByDay.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No clients with a case yet.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 160 }}>Date</TableCell>
                      <TableCell>Clients</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {intakeByDay.map((row) => (
                      <TableRow key={row.date}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateOnly(row.date)}</TableCell>
                        <TableCell>{row.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Collapse>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSearched && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="textSecondary">
                    {users.length === 0 ? 'No clients found' : `${users.length} client${users.length === 1 ? '' : 's'} found`}
                  </Typography>
                </Box>
              )}

              {users.length === 0 && hasSearched ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No clients found matching your search criteria.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }} />
                          <TableCell>Name</TableCell>
                          <TableCell sortDirection={sortBy === 'User_IntakeDate' ? sortDir : false}>
                            <TableSortLabel
                              active={sortBy === 'User_IntakeDate'}
                              direction={sortBy === 'User_IntakeDate' ? sortDir : 'desc'}
                              onClick={() => handleSortHeaderClick('User_IntakeDate')}
                            >
                              Date hired
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>County</TableCell>
                          <TableCell>Case #</TableCell>
                          <TableCell>Source</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {users.map((user) => {
                          const isExpanded = expandedUsers.has(user.User_guid);
                          const cases = casesByUser[user.User_guid];
                          const casesLoading = casesLoadingFor.has(user.User_guid);
                          return (
                          <React.Fragment key={user.User_guid}>
                          <TableRow
                            hover
                            sx={{ cursor: 'pointer', '& td': { borderBottom: isExpanded ? 'none' : undefined } }}
                          >
                            <TableCell sx={{ pr: 0 }} onClick={(e) => { e.stopPropagation(); toggleExpanded(user.User_guid); }}>
                              <IconButton size="small" aria-label={isExpanded ? 'Hide cases' : 'Show cases'}>
                                {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/user?User_guid=${user.User_guid}`)}>
                              {clientDisplayName(user)}
                              {!user.User_Active && (
                                <Chip label="Inactive" color="default" size="small" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ whiteSpace: 'nowrap' }}
                              onClick={() => navigate(`/user?User_guid=${user.User_guid}`)}
                            >
                              {/*
                                A dash, not formatDateOnly's "Not provided". A client with no case
                                has no intake date to provide, and Lloyd was told on 21 Sep that
                                those rows would show a dash.
                              */}
                              {user.User_IntakeDate ? formatDateOnly(user.User_IntakeDate) : '—'}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.User_Email || ''}>
                              {user.User_Email ? (
                                <MuiLink href={`mailto:${user.User_Email}`} onClick={stop} underline="hover">
                                  {user.User_Email}
                                </MuiLink>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              {user.User_PhoneNumber ? (
                                <Box sx={{ display: 'flex', gap: 1, whiteSpace: 'nowrap' }}>
                                  <MuiLink href={`tel:${user.User_PhoneNumber}`} onClick={stop} underline="hover">
                                    {user.User_PhoneNumber}
                                  </MuiLink>
                                  <MuiLink href={`sms:${user.User_PhoneNumber}`} onClick={stop} underline="hover">
                                    Text
                                  </MuiLink>
                                </Box>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            {/*
                              Kimberly, 14 Sep: replace DOB/DL#/License with County and Case #, since
                              a client can hold several cases in different counties, ALL of them show,
                              comma-joined, rather than only the most recent — she never asked for
                              only one, and showing all is the safer default.
                            */}
                            <TableCell onClick={() => navigate(`/user?User_guid=${user.User_guid}`)}>
                              {user.User_Cases?.length
                                ? [...new Set(user.User_Cases.map((c) => c.county || 'Not recorded'))].join(', ')
                                : '—'}
                            </TableCell>
                            <TableCell onClick={() => navigate(`/user?User_guid=${user.User_guid}`)}>
                              {user.User_Cases?.length
                                ? user.User_Cases.map((c) => c.Ticket_Number).join(', ')
                                : '—'}
                            </TableCell>
                            <TableCell onClick={() => navigate(`/user?User_guid=${user.User_guid}`)}>{user.User_Source || '—'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={8} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ pl: 6, pr: 2, py: 1.5 }}>
                                  {casesLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                      <CircularProgress size={18} />
                                    </Box>
                                  ) : !cases || cases.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No cases for this client.
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      {/*
                                        Headers on the nested case rows (Lloyd, Aug 19: "a blue box
                                        appears under DOB"). The rows were unlabelled, so the stage
                                        chip lined up under the client table's DOB column and read
                                        as a stray blue box rather than the case's stage.
                                      */}
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ width: 140 }}>Case #</TableCell>
                                          <TableCell sx={{ width: 110 }}>Ticket #</TableCell>
                                          <TableCell>Charge</TableCell>
                                          <TableCell sx={{ width: 160 }}>Stage</TableCell>
                                          <TableCell sx={{ width: 120 }}>Court date</TableCell>
                                          <TableCell sx={{ width: 180 }}>Court</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {cases.map((ct) => {
                                          const closed = isClosedStage(ct.ticket, ct.ticketStatus?.TicketStatus_Name);
                                          return (
                                            <TableRow
                                              key={ct.ticket.Ticket_guid}
                                              hover
                                              sx={{ cursor: 'pointer', opacity: closed ? 0.55 : 1 }}
                                              onClick={() => navigate(`/ticket?Ticket_guid=${ct.ticket.Ticket_guid}`)}
                                            >
                                              <TableCell sx={{ width: 140 }}>
                                                <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                                                  {ct.ticket.Ticket_MatterNumber || ct.ticket.Ticket_Number || '—'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell sx={{ width: 110 }}>
                                                {ct.ticket.Ticket_Number || '—'}
                                              </TableCell>
                                              <TableCell>
                                                {ct.ticket.Ticket_Description || ct.ticket.Ticket_Citation || '—'}
                                              </TableCell>
                                              <TableCell sx={{ width: 160 }}>
                                                <Chip
                                                  // `nextCourtEvent` is only ever a FUTURE event
                                                  // (TicketController.getNextCourtEvent), so its
                                                  // presence is the court-date floor. Without it
                                                  // this chip and the cases list said different
                                                  // things about the same case.
                                                  label={deriveStageLabel(
                                                    ct.ticket,
                                                    ct.ticketStatus?.TicketStatus_Name,
                                                    Boolean(ct.nextCourtEvent),
                                                  )}
                                                  color={closed ? 'default' : 'info'}
                                                  size="small"
                                                />
                                              </TableCell>
                                              <TableCell sx={{ width: 120 }}>
                                                {ct.ticket.Ticket_CourtDate
                                                  ? new Date(ct.ticket.Ticket_CourtDate).toLocaleDateString('en-US')
                                                  : '—'}
                                              </TableCell>
                                              <TableCell sx={{ width: 180 }}>
                                                {ct.court?.Court_Name || '—'}
                                              </TableCell>
                                            </TableRow>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                          </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Load More Button */}
                  {hasMoreData && (
                    <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                      >
                        {loadingMore ? 'Loading...' : `Load More (Showing ${users.length} clients)`}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default ManagerUsersView;
